import styled from "styled-components";
import { TableWithPagination } from "../../components/TableWithPagination";
import TotalCard from "../../components/TotalCard";
import Search from "../../components/Search";
import { Colors } from "../../config/Constants";
import { useEffect, useState } from "react";
import { getCustomerTransaction } from "../../services/transactionService";
import { getFormattedCustomerTransactionData } from "../../helper";
import ErrorSuccessModal from "../../components/Modal/ErrorSuccessModal";
import ListWithPagination from "../../components/ListsWithPagination";

const columns = [
  {
    path: "name",
    label: "Customer Name",
  },
  {
    path: "id",
    label: "Customer ID",
  },
  {
    path: "date",
    label: "Transaction Date & Time",
  },
  {
    path: "amount",
    label: "Transaction Amount",
  },
];

const Customer = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [isFailureModal, setIsFailureModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    heading: "",
    subHeading: "",
  });
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [deletedCustomers, setDeletedCustomers] = useState(0);
  const [inActiveCustomers, setInActiveCustomers] = useState(0);
  const [totalActiveCustomers, setTotalActiveCustomers] = useState(0);
  const [totalTransaction, setTotalTransaction] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isTableLoading, setTableLoading] = useState(false);
  const limit = 10;

  useEffect(() => {
    getApiData(1, limit, search);
  }, []); // eslint-disable-line

  const getApiData = async (
    currentPage: number,
    limit: number,
    search: string
  ) => {
    setTableLoading(true);
    try {
      const response = await getCustomerTransaction(currentPage, limit, search);
      if (response.status === 200) {
        setTableLoading(false);
        setTotalCustomers(response.data.totalCustomers);
        setDeletedCustomers(response.data.deletedCustomers);
        setInActiveCustomers(response.data.inActiveCustomers);
        setTotalActiveCustomers(response.data.totalActiveCustomers);
        setTotalTransaction(response.data.count);
        setData(getFormattedCustomerTransactionData(response.data.data));
      }
    } catch (error: any) {
      setTableLoading(false);
      if (navigator.onLine) {
        setIsFailureModal(true);
        setErrorMessage({
          heading: "Something went wrong.",
          subHeading: error.message,
        });
      }
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
    getApiData(1, limit, value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    getApiData(page, limit, search);
  };

  return (
    <>
      <PaddingWrapper>
        <HeaderWrapper>
          <CustomerHeader>Customer</CustomerHeader>
          <ActionWrapper>
            <Search
              placeholder="Search"
              onChange={handleSearch}
              value={search}
            />
          </ActionWrapper>
        </HeaderWrapper>
        <CardWrapper>
          <TotalCard
            cardText="Total Customer"
            cardCount={totalCustomers}
            color={Colors.tangerine}
            bgcolor={Colors.lightTangerine}
            active={true}
          />
          <TotalCard
            cardText="Active Customer"
            cardCount={totalActiveCustomers}
            color={Colors.appleGreen}
            bgcolor={Colors.lightAppleGreen}
          />
          <TotalCard
            cardText="Inactive Customer"
            cardCount={inActiveCustomers}
            color={Colors.pureRed}
            bgcolor={Colors.lightPureRed}
          />
          <TotalCard
            cardText="Deleted Customer"
            cardCount={deletedCustomers}
            color={Colors.ultraMarineBlue}
            bgcolor={Colors.lightUltraMarineBlue}
          />
        </CardWrapper>
        <TableWrapper>
          <TableWithPagination
            columns={columns}
            onPageChange={(page) => handlePageChange(page)}
            totalData={totalTransaction}
            currentPage={currentPage}
            rowsPerPage={limit}
            data={data}
            isTableDataLoading={isTableLoading}
          />
        </TableWrapper>
      </PaddingWrapper>
      <ListWrapper>
        <ListWithPagination
          columns={columns}
          onPageChange={(page: number) => handlePageChange(page)}
          totalData={totalTransaction}
          currentPage={currentPage}
          rowsPerPage={limit}
          data={data}
          isTableDataLoading={isTableLoading}
          title={"Customer details"}
          onRowClick={undefined}
        />
      </ListWrapper>

      {isFailureModal && (
        <ErrorSuccessModal
          variant="error"
          heading={errorMessage.heading}
          subHeading={errorMessage.subHeading}
          buttonLabel="Ok"
          setOpenOrClose={setIsFailureModal}
          onSubmit={() => setIsFailureModal(false)}
        />
      )}
    </>
  );
};

const ListWrapper = styled.div`
  display: none;
  @media only screen and (max-width: 768px) {
    display: block;
  }
`;

const PaddingWrapper = styled.div`
  @media only screen and (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CardWrapper = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  overflow-x: auto;
  &::-webkit-scrollbar {
    width: 0.875rem;
    -webkit-appearance: none;
  }

  &::-webkit-scrollbar-thumb {
    height: 3.625rem;
    border: 4px solid rgba(0, 0, 0, 0);
    background-clip: padding-box;
    border-radius: 0.625rem;
    background-color: #000000;
  }
  @media only screen and (max-width: 768px) {
    flex-direction: column;
  }
`;
const TableWrapper = styled.div`
  display: flex;
  padding-top: 4.5rem;
  flex-direction: column;
  @media only screen and (max-width: 768px) {
    display: none;
  }
`;
const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;
const CustomerHeader = styled.div`
  font-size: 1.25rem;
  font-family: "Poppins-Medium";
  line-height: 1.75rem;
  @media only screen and (max-width: 768px) {
    display: none;
  }
`;
const ActionWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;

export default Customer;
